/**
 * Il dettaglio di un task sul telefono (7/10/2026): risponde a "cosa c'è da
 * fare, per quando, e chi lo fa?", come il dettaglio di un promemoria.
 *
 * Prima, a 390px, era un modulo: ASSEGNATO A / SCADENZA / PROMEMORIA /
 * RICORRENZA / PRIORITÀ, cinque campi uno sotto l'altro con le etichette
 * maiuscole. Qui:
 *   1. il titolo grande, e sotto se è da fare, chi l'ha creato e quando;
 *   2. "Quando e chi": una riga per cosa, col valore a destra; toccandola si
 *      apre dal basso il pannello con le scelte (salva subito, come prima);
 *   3. la descrizione, e a cosa è collegato (cliente, progetto);
 *   4. Completa in fondo; Google Calendar, .ics ed Elimina nel menu ⋯ in alto.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { PageActions } from '../../components/layout/PageActions';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { fmtDate } from '../../lib/utils';
import { MCampo, MEmpty, MGroup, MHead, MPage, MRow, MScelte, leggibile } from '../../components/mobile/MobileKit';
import {
  taskDetailQuery, assignableUsersQuery, clientsLiteQuery,
  RECURRENCES, RECURRENCE_LABEL,
  dueInfo, toDateInput, toDateTimeInput, fromDateTimeInput, fmtDateTime, fullName, isDone, isHigh,
  googleCalendarUrl, downloadTaskIcs,
  type TaskRecurrenceKey,
} from './tasksApi';
import { useTaskActions } from './useTaskActions';

type Pannello = null | 'assegnato' | 'scadenza' | 'promemoria' | 'ricorrenza' | 'priorita' | 'descrizione' | 'cliente' | 'titolo';

export function TaskDetailMobile({ id }: { id: string }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { complete, reopen, update, remove } = useTaskActions();
  const hasWrite = useMemo(() => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === ModuleKey.TASKS)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.WRITE);
  }, [user]);
  const { data: task, isLoading, isError } = useQuery({ ...taskDetailQuery(id), retry: false });
  const { data: users } = useQuery(assignableUsersQuery);
  const { data: clientsData } = useQuery(clientsLiteQuery);
  const [aperto, setAperto] = useState<Pannello>(null);
  const [bozza, setBozza] = useState('');

  if (isError) return <MPage><MEmpty>Task non trovato: forse è stato eliminato, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !task) return <MPage><MEmpty>Carico il task…</MEmpty></MPage>;

  const canWrite = hasWrite && (task.canManage ?? true);
  const done = isDone(task.status);
  const due = dueInfo(task.dueDate, done);
  const gcal = googleCalendarUrl(task);
  const patch = (data: Record<string, unknown>) => update.mutate({ id: task.id, data });
  const chiudi = () => setAperto(null);
  const apri = (p: Pannello, valore = '') => { if (!canWrite) return; setBozza(valore); setAperto(p); };

  const actionItems: ActionItem[] = [
    { key: 'gcal', label: 'Aggiungi a Google Calendar', icon: 'calendarPlus', hidden: !gcal, onClick: () => window.open(gcal!, '_blank', 'noopener') },
    { key: 'ics', label: 'Scarica il file per il calendario (.ics)', icon: 'export', hidden: !task.dueDate && !task.remindAt, onClick: () => { downloadTaskIcs(task.id, task.title).catch((e: any) => toast.alert('File non scaricato', e?.message ?? 'Riprova')); } },
    {
      key: 'delete', label: 'Elimina il task', icon: 'trash', danger: true, hidden: !canWrite,
      onClick: async () => { const r = await remove.mutateAsync({ id: task.id, title: task.title }); if (r) navigate('/tasks'); },
    },
  ];

  const scadenzaTesto = !task.dueDate ? 'nessuna' : due.tone === 'alert' ? due.label : fmtDate(task.dueDate);
  const sotto = [
    done ? `completato${task.completedAt ? ` il ${fmtDateTime(task.completedAt)}` : ''}` : 'da fare',
    task.createdBy ? `creato da ${fullName(task.createdBy)}` : null,
    task.createdAt ? `il ${fmtDate(task.createdAt)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <>
      {/* Il menu ⋯ nella barra in alto (PageActions lo porta lassù sul telefono). */}
      <PageActions className="flex gap-2">
        <ActionsMenu variant="ghost" label="Azioni" items={actionItems} disabled={remove.isPending} />
      </PageActions>

      <MPage>
        <MHead title={task.title} sub={sotto} done={done} onClick={canWrite ? () => apri('titolo', task.title) : undefined} />

        {/* I valori a destra sono grigi, come nelle impostazioni del telefono: il rosso solo per la scadenza passata. */}
        <MGroup title="Quando e chi">
          <MRow icon="user" title="Chi lo fa" trail={task.assignee ? fullName(task.assignee) : 'nessuno'} trailTone="mute" onClick={canWrite ? () => apri('assegnato') : undefined} />
          <MRow icon="calendar" tone={due.tone === 'alert' && !done ? 'alert' : undefined} title="Scadenza" trail={scadenzaTesto} trailTone={due.tone === 'alert' && !done ? 'alert' : 'mute'} onClick={canWrite ? () => apri('scadenza', toDateInput(task.dueDate)) : undefined} />
          <MRow icon="bell" title="Promemoria" trail={task.remindAt ? fmtDateTime(task.remindAt) : 'nessuno'} trailTone="mute" onClick={canWrite ? () => apri('promemoria', toDateTimeInput(task.remindAt)) : undefined} />
          <MRow icon="repeat" title="Si ripete" trail={RECURRENCE_LABEL[task.recurrence ?? 'NONE']} trailTone="mute" onClick={canWrite ? () => apri('ricorrenza') : undefined} />
          <MRow icon="alert" tone={isHigh(task.priority) ? 'alert' : undefined} title="Priorità" trail={isHigh(task.priority) ? 'Alta' : 'Normale'} trailTone={isHigh(task.priority) ? 'alert' : 'mute'} onClick={canWrite ? () => apri('priorita') : undefined} />
        </MGroup>

        <MGroup title="Descrizione" action={canWrite ? { label: task.description ? 'Modifica' : 'Aggiungi', onClick: () => apri('descrizione', task.description ?? '') } : undefined}>
          {task.description
            ? <div className="mk-text">{task.description}</div>
            : <MEmpty>Nessuna descrizione.</MEmpty>}
        </MGroup>

        <MGroup title="Collegato a">
          <MRow icon="building" title="Cliente" trail={task.client?.shortName ? leggibile(task.client.shortName) : 'nessuno'} trailTone="mute" onClick={canWrite ? () => apri('cliente') : undefined} />
          {task.project && (
            <MRow icon="cube" title={task.project.name.split(/\s+[—–-]\s+/).slice(1).join(' ') || task.project.name} sub={`progetto ${task.project.code}`} onClick={() => navigate(`/projects/${task.project!.id}`)} />
          )}
        </MGroup>
      </MPage>

      {canWrite && (
        <div className="m-actionbar m-only">
          {done ? (
            <button className="btn" disabled={reopen.isPending} onClick={() => reopen.mutate({ id: task.id, title: task.title })}>
              <Icon name="undo" size={12} /> Riapri
            </button>
          ) : (
            <button className="btn btn-primary" disabled={complete.isPending} onClick={() => complete.mutate({ id: task.id, title: task.title, prevStatus: task.status, dueDate: task.dueDate, recurrence: task.recurrence })}>
              <Icon name="check" size={12} /> Completa
            </button>
          )}
        </div>
      )}

      {/* ── I pannelli: uno per cosa, salvano subito come la pagina del computer ── */}
      <MScelte
        open={aperto === 'assegnato'}
        onClose={chiudi}
        titolo="Chi lo fa"
        opzioni={[{ v: '', l: 'Nessuno' }, ...(users ?? []).map((u) => ({ v: u.id, l: `${fullName(u)}${u.id === user?.id ? ' (io)' : ''}` }))]}
        valore={task.assignee?.id ?? ''}
        onScegli={(v) => { patch({ assigneeId: v || null }); chiudi(); }}
      />
      <MScelte
        open={aperto === 'ricorrenza'}
        onClose={chiudi}
        titolo="Si ripete"
        nota="Quando lo completi, si crea da solo il prossimo."
        opzioni={RECURRENCES.map((r) => ({ v: r, l: RECURRENCE_LABEL[r] }))}
        valore={task.recurrence ?? 'NONE'}
        onScegli={(v) => { patch({ recurrence: v as TaskRecurrenceKey }); chiudi(); }}
      />
      <MScelte
        open={aperto === 'priorita'}
        onClose={chiudi}
        titolo="Priorità"
        opzioni={[{ v: 'MEDIA', l: 'Normale' }, { v: 'ALTA', l: 'Alta' }]}
        valore={isHigh(task.priority) ? 'ALTA' : 'MEDIA'}
        onScegli={(v) => { patch({ priority: v }); chiudi(); }}
      />
      <MScelte
        open={aperto === 'cliente'}
        onClose={chiudi}
        titolo="Cliente"
        opzioni={[{ v: '', l: 'Nessuno' }, ...(clientsData?.items ?? []).map((c) => ({ v: c.id, l: leggibile(c.shortName) }))]}
        valore={task.client?.id ?? ''}
        onScegli={(v) => { patch({ clientId: v || null }); chiudi(); }}
      />
      <MCampo
        open={aperto === 'scadenza'}
        onClose={chiudi}
        titolo="Scadenza"
        campo={<input className="inp" type="date" value={bozza} onChange={(e) => setBozza(e.target.value)} />}
        onSalva={() => { patch({ dueDate: bozza || null }); chiudi(); }}
        onTogli={task.dueDate ? () => { patch({ dueDate: null }); chiudi(); } : undefined}
        togli="Togli la scadenza"
      />
      <MCampo
        open={aperto === 'promemoria'}
        onClose={chiudi}
        titolo="Promemoria"
        nota="Ti arriva un avviso a quell'ora."
        campo={<input className="inp" type="datetime-local" value={bozza} onChange={(e) => setBozza(e.target.value)} />}
        onSalva={() => { patch({ remindAt: fromDateTimeInput(bozza) }); chiudi(); }}
        onTogli={task.remindAt ? () => { patch({ remindAt: null }); chiudi(); } : undefined}
        togli="Togli il promemoria"
      />
      <MCampo
        open={aperto === 'descrizione'}
        onClose={chiudi}
        titolo="Descrizione"
        campo={<textarea className="inp" rows={6} value={bozza} placeholder="Di cosa si tratta, cosa serve per chiuderlo…" onChange={(e) => setBozza(e.target.value)} />}
        onSalva={() => { patch({ description: bozza.trim() || null }); chiudi(); }}
      />
      <MCampo
        open={aperto === 'titolo'}
        onClose={chiudi}
        titolo="Titolo del task"
        campo={<input className="inp" value={bozza} onChange={(e) => setBozza(e.target.value)} />}
        onSalva={() => { const t = bozza.trim(); if (t && t !== task.title) patch({ title: t }); chiudi(); }}
      />
    </>
  );
}

